/**
 * Emblema de la marca.
 *
 * LA IDEA
 * Un sello: placa azul con bisel —luz arriba, sombra abajo, como una tecla
 * fisica— y dentro la cruz de Farmacia San Carlos, en dos piezas que se
 * abrazan en diagonal: la de arriba en blanco y la de abajo en el verde del
 * letrero.
 *
 * Una cruz sola es de cualquier farmacia; las dos piezas con su ranura son de
 * esta. La geometria y el porque de cada medida estan en `lib/brand-mark.ts`.
 *
 * EL COMPONENTE TRAE SU PLACA
 * Antes el emblema era un glifo suelto que cada pantalla metia dentro de un
 * cuadro `bg-primary`. Ahora la placa es parte del logotipo, con su degradado y
 * su bisel, porque un fondo plano de color no es lo mismo que un sello y la
 * diferencia se nota justo en las tres pantallas donde se ve grande.
 *
 * LOS IDENTIFICADORES SON UNICOS POR INSTANCIA
 * Los degradados de un SVG se referencian por `id`, y los `id` son globales al
 * documento. Con dos emblemas en pantalla —la barra lateral y una hoja modal,
 * por ejemplo— el segundo pisaria las definiciones del primero y ambos acabarian
 * pintados con el ultimo degradado declarado. `useId` evita esa colision.
 *
 * LA ANIMACION
 * La placa entra creciendo, el glifo asienta detras de ella y un destello cruza
 * la superficie una sola vez. Poco mas de medio segundo, y ocurre UNA vez.
 *
 * Se anima con anime.js porque es una linea de tiempo puramente SVG,
 * independiente del ciclo de vida de React; framer-motion se reserva para las
 * entradas de componentes, donde si importa el montaje y desmontaje.
 *
 * Con `prefers-reduced-motion` no se anima nada: el sello aparece ya puesto.
 * Nunca queda a medias.
 */

import { useEffect, useId, useRef, type ReactNode } from 'react';
import { animate } from 'animejs';
import { DURATION, prefersReducedMotion } from '../lib/motion.js';
import { MARK_COLORS, MARK_LOWER_PATH, MARK_UPPER_PATH } from '../lib/brand-mark.js';
import { cx } from './ui.js';

export function BrandLogo({
  className,
  animated = false,
}: {
  className?: string;
  /** Anima la entrada una sola vez al montarse. */
  animated?: boolean;
}): ReactNode {
  const root = useRef<SVGSVGElement | null>(null);

  // `useId` devuelve algo como «r1»: valido como id de SVG, pero con caracteres
  // que estorban dentro de `url(#...)`. Se dejan solo letras y numeros.
  const uid = `sc-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;

  useEffect(() => {
    if (!animated || !root.current) return;
    if (prefersReducedMotion()) return;

    const tile = root.current.querySelectorAll<SVGGElement>('[data-tile]');
    const glyph = root.current.querySelectorAll<SVGGElement>('[data-glyph]');
    const sweep = root.current.querySelectorAll<SVGRectElement>('[data-sweep]');

    animate(tile, {
      scale: [0.9, 1],
      opacity: [0, 1],
      duration: DURATION.normal * 1000,
      ease: 'outExpo',
    });

    animate(glyph, {
      scale: [0.82, 1],
      opacity: [0, 1],
      duration: DURATION.slow * 1000,
      delay: 90,
      ease: 'outBack',
    });

    // El destello arranca fuera de la placa y sale por el otro lado. Recorre
    // unidades del propio SVG, no pixeles de pantalla, asi que se ve igual a
    // 18 px en la barra lateral que a 64 en el acceso.
    animate(sweep, {
      translateX: [0, 104],
      duration: DURATION.slow * 1000 + 300,
      delay: 240,
      ease: 'inOutQuad',
    });
  }, [animated]);

  return (
    <svg
      ref={root}
      viewBox="0 0 64 64"
      className={cx('block', className)}
      role="img"
      aria-label="Emblema del negocio"
    >
      {/*
        `fill-box` hace que scale use cada figura como referencia y no el lienzo
        entero: sin esto el glifo crece desde la esquina del SVG y se sale.
      */}
      <style>{`
        [data-tile], [data-glyph] { transform-box: fill-box; transform-origin: center center; }
      `}</style>

      <defs>
        <linearGradient id={`${uid}-plate`} x1="0.05" y1="0" x2="0.9" y2="1">
          <stop offset="0" stopColor={MARK_COLORS.plate[0]} />
          <stop offset="0.5" stopColor={MARK_COLORS.plate[1]} />
          <stop offset="1" stopColor={MARK_COLORS.plate[2]} />
        </linearGradient>

        {/* Bisel: un solo trazo que pasa de luz arriba a sombra abajo. */}
        <linearGradient id={`${uid}-bevel`} x1="0.5" y1="0" x2="0.5" y2="1">
          <stop offset="0" stopColor="#ffffff" stopOpacity="0.6" />
          <stop offset="0.45" stopColor="#ffffff" stopOpacity="0" />
          <stop offset="1" stopColor="#000000" stopOpacity="0.32" />
        </linearGradient>

        <linearGradient id={`${uid}-glyph`} x1="0.2" y1="0" x2="0.7" y2="1">
          <stop offset="0" stopColor={MARK_COLORS.upper[0]} />
          <stop offset="1" stopColor={MARK_COLORS.upper[1]} />
        </linearGradient>

        <linearGradient id={`${uid}-green`} x1="0.3" y1="0" x2="0.7" y2="1">
          <stop offset="0" stopColor={MARK_COLORS.lower[0]} />
          <stop offset="1" stopColor={MARK_COLORS.lower[1]} />
        </linearGradient>

        <linearGradient id={`${uid}-sweep`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#ffffff" stopOpacity="0" />
          <stop offset="0.5" stopColor="#ffffff" stopOpacity="0.34" />
          <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
        </linearGradient>

        <clipPath id={`${uid}-clip`}>
          <rect x="2" y="2" width="60" height="60" rx="15" />
        </clipPath>
      </defs>

      <g data-tile>
        <rect x="2" y="2" width="60" height="60" rx="15" fill={`url(#${uid}-plate)`} />

        {/*
          La inclinacion vive en un grupo aparte y no en el propio rectangulo:
          anime.js escribe `transform` como propiedad CSS, y una transformacion
          CSS reemplaza por completo al atributo `transform` del elemento. Si la
          rotacion estuviera en el rectangulo, el destello la perderia en el
          primer cuadro de la animacion y cruzaria recto.
        */}
        <g clipPath={`url(#${uid}-clip)`}>
          <g transform="rotate(18 32 32)">
            {/* En reposo queda a la izquierda de la placa, fuera de vista. */}
            <rect data-sweep x="-30" y="-16" width="24" height="96" fill={`url(#${uid}-sweep)`} />
          </g>
        </g>

        <g data-glyph>
          <path d={MARK_UPPER_PATH} fill={`url(#${uid}-glyph)`} />
          <path d={MARK_LOWER_PATH} fill={`url(#${uid}-green)`} />
        </g>

        <rect
          x="2.75"
          y="2.75"
          width="58.5"
          height="58.5"
          rx="14.4"
          fill="none"
          stroke={`url(#${uid}-bevel)`}
          strokeWidth="1.5"
        />
      </g>
    </svg>
  );
}
